import { useCallback, useEffect, useState } from "react";
import { useListSearch } from "../../../common/hooks";
import { useNavigate } from "react-router-dom";
import { connect } from "react-redux";
import { useIntl } from "react-intl";
import TooltipButton from "../../../common/tooltipButton";
import {
  listRequest,
  deleteRequest,
  setQuery,
} from "../../../../store/slices/certificateSlice";
import CustomBreadCrumbs from "../../../common/breadcrumbs";
import * as RoleHelper from "../../../../helpers/RoleHelper";
import * as ListCellRenders from "../../../common/list/ListCellRenders";
import { AccessScopeConstants, RouteConstants } from "../../../../constants";
import List from "../../../common/list";
import { useMountEffect } from "primereact/hooks";
import { Dialog } from "primereact/dialog";
import PreviewCertificate from "../preview";
import { confirmDialog, ConfirmDialog } from "primereact/confirmdialog";
import { TextInput } from "../../../common/fields";
import useMobileView from "../../../common/hooks/useMobileView";

const CertificateList = ({
  labels,
  certificates = [],
  user,
  action,
  error,
  requesting,
  listRequest,
  deleteRequest,
  setQuery,
  query,
}) => {
  const navigate = useNavigate();
  const intl = useIntl();

  const { isMobile } = useMobileView();

  const [showPreview, setShowPreview] = useState(false);
  const [selectedCertificate, setSelectedCertificate] = useState(null);
  const [createdByNamePattern, setCreatedByNamePattern] = useListSearch(
    query?.createdBy,
    (value) => setQuery({ ...query, createdBy: value, pageNo: 1 })
  );
  const [namePattern, setNamePattern] = useListSearch(
    query?.namePattern,
    (value) => setQuery({ ...query, namePattern: value, pageNo: 1 })
  );

  const { role } = user || {};

  const canAdd = RoleHelper.checkRoleScope(
    role?.scope,
    AccessScopeConstants.ContentAccess,
    AccessScopeConstants.CreateScope
  );

  const canDelete = RoleHelper.checkRoleScope(
    role?.scope,
    AccessScopeConstants.ContentAccess,
    AccessScopeConstants.DeleteScope
  );

  const fetchList = useCallback(() => {
    listRequest({
      ...query,
      sortColumn: query.sortColumn || "name",
    });
  }, [query, listRequest]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  useEffect(() => {
    if (!error && action === "delete" && !requesting) {
      listRequest({
        ...query,
        sortColumn: query.sortColumn || "name",
      });
    }
  }, [error, action, requesting]);

  const handleAddCertificates = () => {
    navigate(RouteConstants.APP_ROUTE_CERTIFICATES_ADD);
  };

  const onPreview = (data) => {
    setSelectedCertificate(data);
    setShowPreview(true);
  };

  const onDownload = (data) => {
    const a = document.createElement("a");
    a.href = `${new URL(data.url, process.env.REACT_APP_URL).toString()}`;
    a.download = data.filename;
    a.target = "_blank";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const onDeleteAction = (data) => {
    confirmDialog({
      message: intl.formatMessage(
        { id: "delete_object_message" },
        {
          name: data.name,
          object: intl.formatMessage({ id: "certificate" }),
        }
      ),
      header: intl.formatMessage({ id: "delete_confirmation" }),
      icon: "pi pi-info-circle",
      defaultFocus: "reject",
      acceptClassName: "p-button-danger",
      rejectClassName: "p-button-secondary",
      accept: () => {
        deleteRequest({ action: "delete", id: data.id });
      },
      reject: () => {},
    });
  };

  return (
    <div>
      <CustomBreadCrumbs labels={labels} navigate={navigate} />
      <div className="edexlms-page-content">
        <div className="edexlms-page-header">
          <div className="edexlms-page-title">
            {intl.formatMessage({ id: "certificates" })}
          </div>
          <div className="edexlms-page-actions">
            <TooltipButton
              icon="pi pi-plus"
              onClick={handleAddCertificates}
              size="small"
              label={intl.formatMessage({ id: "add_certificate" })}
              type="button"
              disabled={!canAdd}
              tooltipOptions={{ showOnDisabled: true, position: "bottom" }}
              tooltip={
                canAdd ? "" : intl.formatMessage({ id: "no_permission" })
              }
            />
          </div>
        </div>
        <div className="edexlms-page-body">
          <div className="edexlms-search-filter-bar">
            <TextInput
              iconPosition="left"
              icon="pi-search"
              placeholder={intl.formatMessage({
                id: "search_certificates",
              })}
              input={{
                name: "search",
                value: namePattern,
                onChange: (e) => setNamePattern(e.target.value),
              }}
            />
            <TextInput
              iconPosition="left"
              icon="pi-search"
              placeholder={intl.formatMessage({
                id: "search_created_by",
              })}
              input={{
                name: "search",
                value: createdByNamePattern,
                onChange: (e) => setCreatedByNamePattern(e.target.value),
              }}
            />
          </div>
          <List
            loading={action === "list" && requesting}
            pagination={false}
            data={certificates || []}
            selectionMode=""
            columns={[
              {
                field: "name",
                header: "name",
                sortable: true,
              },
              {
                field: "template",
                header: "template",
                sortable: false,
                body: (data) => {
                  return (
                    <img
                      style={{
                        height: 50,
                        width: "auto",
                        border: `1px solid #DFE7EF`,
                      }}
                      src={`${new URL(
                        data.url,
                        process.env.REACT_APP_URL
                      ).toString()}?v=${data.updatedAt}`}
                      alt={data.name}
                    />
                  );
                },
              },
              {
                field: "createdByUser",
                header: "created_by",
                sortable: false,
                body: ListCellRenders.CreatedBy,
              },
              {
                field: "createdAt",
                header: "created_at",
                sortable: true,
                body: (data) => {
                  if (!data.isSystemDefined) {
                    return ListCellRenders.DateTime("createdAt")(data);
                  } else {
                    return "-";
                  }
                },
              },
              {
                field: "updatedAt",
                header: "updated_at",
                sortable: true,
                body: (data) => {
                  if (!data.isSystemDefined) {
                    return ListCellRenders.DateTime("updatedAt")(data);
                  } else {
                    return "-";
                  }
                },
              },
              {
                field: "actions",
                headerIcon: "pi-cog",
                align: "center",
                body: ListCellRenders.OtherActions([
                  {
                    label: "preview",
                    icon: "pi pi-eye",
                    isAllowed: true,
                    action: (data) => onPreview(data),
                  },
                  {
                    label: "download_template",
                    icon: "pi pi-download",
                    isAllowed: true,
                    action: (data) => onDownload(data),
                  },
                  {
                    label: "delete",
                    icon: "pi pi-trash",
                    isAllowed: canDelete,
                    additionalCheckNotAllowedMessage: (data) =>
                      data.isSystemDefined
                        ? "certification_system_defined_delete"
                        : "certification_in_user_delete",
                    additionalCheckForAllowed: (data) =>
                      !data.isSystemDefined && data.canBeDeleted,
                    action: (data) => onDeleteAction(data),
                  },
                ]),
              },
            ]}
          />
        </div>
      </div>
      <Dialog
        className="preview_cert"
        header={intl.formatMessage({ id: "preview_certificate" })}
        visible={showPreview}
        style={{
          height: isMobile ? "480px" : "700px",
          width: isMobile ? "380px" : "600px",
          minHeight: isMobile ? "480px" : "700px",
          minWidth: isMobile ? "380px" : "600px",
        }}
        onHide={() => setShowPreview(false)}
      >
        <PreviewCertificate
          url={selectedCertificate?.url}
          orientation={selectedCertificate?.orientation}
          isActual={false}
          selectedCertificate={selectedCertificate}
        />
      </Dialog>
      <ConfirmDialog />
    </div>
  );
};

const mapStateToProps = (state) => ({
  certificates: state.certificates.data,
  action: state.certificates.action,
  requesting: state.certificates.requesting,
  error: state.certificates.error,
  user: state.self?.user,
  query: state.certificates?.query || {},
});

const mapDispatchToProps = (dispatch) => ({
  listRequest: (data) => dispatch(listRequest(data)),
  deleteRequest: (data) => dispatch(deleteRequest(data)),
  setQuery: (data) => dispatch(setQuery(data)),
});

export default connect(mapStateToProps, mapDispatchToProps)(CertificateList);
